import React, {useEffect} from 'react'
import {
  ErrorBoundary,
  useCurrentUser,
  useLocalStorage,
  UserAnPCheck,
  useToggle,
  useUrlState,
  useUrlStateDebounced,
} from '@therms/react-modules'
import {Button, Icon, useIsBreakpointActive} from '@therms/atalaya'
import {EventsTabsLayout} from '@src/modules/Events/router/screens/Index/components/EventsTabsLayout'
import {EventsColumnsLayout} from '@src/modules/Events/router/screens/Index/components/EventsColumnsLayout'
import {NewEventModal} from '@src/modules/Events/components/NewEventModal'
import {EventsSearchFilters} from '@src/modules/Events/router/screens/Index/components/EventsSearchFilters'
import {BulkDownloadEventsContextMenu} from '@src/modules/Events/components/BulkDownloadEventsContextMenu'
import {EventInteractiveModal} from '@src/modules/Events/shared/EventInteractiveModal'
import {IoMdAdd} from 'react-icons/io'
import {MdViewColumn, MdViewList} from 'react-icons/md'
import {useSetState, useUpdateEffect} from 'ahooks'

interface EventsIndexProps {
  hideNewEventButton?: boolean
}

function EventsIndex({hideNewEventButton}: EventsIndexProps) {
  const allowLayouts = useIsBreakpointActive('lg')
  const [layout, setLayout] = useLocalStorage<'tabs' | 'columns'>(
    'events-index-layout',
    'columns',
  )
  const [showEventModalId, setShowEventModalId] = useUrlState('eventid', '')
  const [showNewEventModal, toggleNewEventModal] = useToggle(false)
  const [searchText, debouncedSearchText, setSearchText] = useUrlStateDebounced(
    'evtsrch',
    '',
  )

  const [assignedToUserFilter, setAssignedToUserFilter] = useUrlState(
    'asgntocrusr',
    false,
  )

  const [filtersState, setFiltersState] = useSetState<{
    selectedActivityTypeIds?: string[] | undefined
    selectedDateTimeRange?: {start?: string; end?: string}
    selectedEventTypeIds?: string[] | undefined
    selectedLocationId?: string | undefined
    selectedUserIds?: string[] | undefined
  }>({
    selectedActivityTypeIds: undefined,
    selectedDateTimeRange: {end: undefined, start: undefined},
    selectedEventTypeIds: undefined,
    selectedLocationId: undefined,
    selectedUserIds: undefined,
  })

  const [urlState, setUrlState] = useUrlState('eventfilters', filtersState)

  useEffect(() => {
    if (urlState) {
      setFiltersState(urlState)
    }
  }, [])

  useUpdateEffect(() => {
    setUrlState(filtersState)
  }, [filtersState])

  const currentUser = useCurrentUser()

  let _assignedToUserFilter

  if (assignedToUserFilter) {
    _assignedToUserFilter = [currentUser.id]
  }

  let assignedUsers

  if (_assignedToUserFilter && filtersState.selectedUserIds?.length) {
    assignedUsers = _assignedToUserFilter.concat(filtersState.selectedUserIds)
  } else if (filtersState.selectedUserIds?.length) {
    assignedUsers = filtersState.selectedUserIds
  } else {
    assignedUsers = _assignedToUserFilter
  }

  return (
    <>
      <div className="gap-xxs flex flex-wrap-reverse justify-between">
        <EventsSearchFilters
          assignedToUserFilter={assignedToUserFilter}
          filtersState={filtersState}
          searchText={searchText}
          setAssignedToUserFilter={setAssignedToUserFilter}
          setFiltersState={setFiltersState}
          setSearchText={setSearchText}
        />

        <div className="gap-x-xxs ml-auto flex min-w-max shrink justify-end">
          {allowLayouts && (
            <div>
              <Button
                onClick={() =>
                  setLayout(layout === 'tabs' ? 'columns' : 'tabs')
                }
                subtle
              >
                <Icon Icon={layout === 'tabs' ? MdViewColumn : MdViewList} />
              </Button>
            </div>
          )}

          <UserAnPCheck
            check={(user) => user.anp.role.ops.access.canDownloadAndExportData}
          >
            <BulkDownloadEventsContextMenu
              activityTypeIds={filtersState.selectedActivityTypeIds}
              dateTimeRange={filtersState.selectedDateTimeRange || {}}
              eventTypeIds={filtersState.selectedEventTypeIds}
              locationId={filtersState.selectedLocationId}
              searchText={debouncedSearchText}
              userIdsAssigned={assignedUsers}
            />
          </UserAnPCheck>

          {!hideNewEventButton && (
            <UserAnPCheck
              check={(user) => user.anp.role.ops.permissions.createEvents}
            >
              <div className="w-full md:w-fit">
                <Button
                  className="w-full"
                  onClick={toggleNewEventModal}
                  variant="main"
                >
                  <Icon Icon={IoMdAdd} size="sm" />

                  <span>New Event</span>
                </Button>
              </div>
            </UserAnPCheck>
          )}
        </div>
      </div>

      <div className="mt-base">
        {layout === 'columns' && allowLayouts ? (
          <EventsColumnsLayout
            activityTypeIds={filtersState.selectedActivityTypeIds}
            dateTimeRange={filtersState.selectedDateTimeRange || {}}
            eventTypeIds={filtersState.selectedEventTypeIds}
            locationId={filtersState.selectedLocationId}
            onClickViewAllClosed={() => {
              setLayout('tabs')
            }}
            onSelectEventId={setShowEventModalId}
            searchText={debouncedSearchText}
            userIdsAssigned={assignedUsers}
          />
        ) : (
          <EventsTabsLayout
            activityTypeIds={filtersState.selectedActivityTypeIds}
            dateTimeRange={filtersState.selectedDateTimeRange || {}}
            eventTypeIds={filtersState.selectedEventTypeIds}
            locationId={filtersState.selectedLocationId}
            onSelectEventId={setShowEventModalId}
            searchText={debouncedSearchText}
            userIdsAssigned={assignedUsers}
          />
        )}
      </div>

      {showEventModalId && (
        <EventInteractiveModal
          closeHandler={() => setShowEventModalId('')}
          eventId={showEventModalId}
        />
      )}

      {showNewEventModal && (
        <ErrorBoundary>
          <NewEventModal
            closeHandler={(eventId) => {
              toggleNewEventModal()

              if (eventId) {
                setShowEventModalId(eventId)
              }
            }}
          />
        </ErrorBoundary>
      )}
    </>
  )
}

export {EventsIndex}
